// API Configuration - Single Source of Truth
// All API calls should import API_BASE_URL from this file
//
// IMPORTANT: API_BASE_URL should NOT include the /api prefix.
// Routes should start with /api (e.g., /api/video-kinematics/...)
// This prevents double /api/api prefixes in URLs.

const getApiBaseUrl = (): string => {
  // Use REACT_APP_API_BASE_URL if set, otherwise default to localhost
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  if (envUrl && envUrl.trim()) {
    let url = envUrl.trim();
    // Remove trailing slash
    url = url.replace(/\/$/, '');
    // Remove /api suffix if present (base URL should not include /api)
    url = url.replace(/\/api$/, '');
    return url;
  }
  
  // Default to localhost for development (NO /api prefix)
  return 'http://localhost:8000';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Helper function to build API URLs.
 * Ensures the endpoint always starts with /api to prevent double /api/api.
 * 
 * @param endpoint - The API endpoint (e.g., '/auth/login' or 'auth/login')
 * @returns Full URL with base + /api + endpoint
 * 
 * @example
 * buildApiUrl('/auth/login') // => 'http://localhost:8000/api/auth/login'
 * buildApiUrl('auth/login')  // => 'http://localhost:8000/api/auth/login'
 */
export function buildApiUrl(endpoint: string): string {
  // Remove leading slash if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // Ensure endpoint starts with 'api/'
  const apiEndpoint = cleanEndpoint.startsWith('api/') 
    ? `/${cleanEndpoint}` 
    : `/api/${cleanEndpoint}`;
  
  const fullUrl = `${API_BASE_URL}${apiEndpoint}`;
  
  // Dev-only: Assert no double /api/api
  if (process.env.NODE_ENV === 'development' && fullUrl.includes('/api/api')) {
    console.error('[buildApiUrl] ERROR: Double /api/api detected:', fullUrl);
    console.error('[buildApiUrl] API_BASE_URL:', API_BASE_URL);
    console.error('[buildApiUrl] endpoint:', endpoint);
  }
  
  return fullUrl;
}

// Debug logging (dev-only, no secrets)
if (process.env.NODE_ENV === 'development') {
  const tokenExists = !!localStorage.getItem('access_token');
  console.log('[API Config] API_BASE_URL:', API_BASE_URL);
  console.log('[API Config] Token exists:', tokenExists);
  console.log('[API Config] Rule: Base URL has no /api; routes start with /api');
  // Test the helper
  const testUrl = buildApiUrl('/video-kinematics/test/debug-video');
  console.log('[API Config] Example URL:', testUrl);
  if (testUrl.includes('/api/api')) {
    console.error('[API Config] ERROR: Test URL has double /api/api!');
  }
}
